/**
 * Onboarding — sign in, web installation, then the plan gate (SPEC.md §9.5).
 *
 * It was three before, then two. The old middle step asked for a vault
 * passphrase and is gone: there is no key to derive, so there is nothing to ask
 * for and no way to be locked out of your own ledger. The third step now is the
 * monthly salary, which is a different kind of question — it does not guard the
 * data, it is the number the whole budget is a fraction of (scope expansion
 * 2026-07-31).
 *
 * No ledger setup beyond those gates: eight categories are seeded on the account, no
 * budgets are set, the cycle defaults to the 1st. Nothing else is asked before
 * the first expense.
 *
 * The install gate is load-bearing (SPEC.md §10.1): Safari evicts
 * script-writable storage after 7 idle days — the session AND unsynced
 * expenses — and installed PWAs are exempt.
 */

import { Engraving } from "@/features/common/engraving";
import { ENGRAVINGS } from "@/features/common/engravings";
import { LegalLinks } from "@/features/common/legal_links";
import { useCurrentPeriod, useProfile } from "@/hooks/use_data";
import { useState } from "react";

import { tryParseAmount } from "@/domain/money";
import { periodStartDate } from "@/domain/period";
import { GhostButton, Label, PrimaryButton } from "@/features/common/primitives";
import { useAdoptSession, useSignOutFlow } from "@/features/onboarding/leave_flow";
import { SignInButtons } from "@/features/onboarding/sign_in_buttons";
import { useAppleSignIn, useGoogleSignIn } from "@/hooks/use_sign_in";
import { installInstructions, useInstallState } from "@/hooks/use_install";
import { setSalary, updateProfile } from "@/lib/repo";

/** The sign-in plate's height, from the stylesheet (`.signin-plate`); its width follows. */
const SIGNIN_PLATE_HEIGHT = "var(--signin-plate-height)";
const PLATE = ENGRAVINGS["open-sea"];

export function SignInView({ familyInvite = false }: { familyInvite?: boolean }): React.JSX.Element {
  // Guarded, because this screen can appear over an existing ledger: the boot
  // fallback lands here when Dexie could not be read. Adopting unchecked would
  // let one account inherit another's expenses; `useAdoptSession` routes every
  // outcome — adopted, another account's pending changes, or a failure.
  const { adopt, failure, clearFailure, sheet } = useAdoptSession();
  const google = useGoogleSignIn(adopt);
  const apple = useAppleSignIn(adopt);
  // Whichever sheet was used last speaks; Google's button is the default.
  const { status, error, retry } =
    apple.status !== "ready" ? { status: apple.status, error: apple.error, retry: apple.reset } : google;

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-phi-3 overflow-x-clip bg-bg px-phi-3 py-phi-3 text-center short:gap-phi-1">
      {/* The whole plate, never a clipped strip of it, as wide as the screen —
          edge to edge, past the page's side padding (hence the clip above). It
          is portrait, so its height is what has to give: capped at
          `SIGNIN_PLATE_HEIGHT` of the viewport, the width follows from the
          plate's own proportions, and a short screen gets a narrower plate
          rather than a sign-in button pushed below the fold. */}
      <Engraving
        name="open-sea"
        className="signin-plate mx-auto w-screen"
        style={{ maxWidth: `calc(${SIGNIN_PLATE_HEIGHT} * ${String(PLATE.width)} / ${String(PLATE.height)})` }}
      />
      {familyInvite ? <p className="text-phi-0 text-tx-2">Sign in, and the invite opens again.</p> : null}
      <Trident />

      <div className="flex flex-col gap-phi-n1">
        <h1 className="text-phi-3 font-bold tracking-tight">Tide Tally</h1>
        <p className="max-w-phi-12 text-phi-n1 text-tx-2">
          Your budget is a tide line. Log what you spend in three taps, and see
          whether you are above it.
        </p>
      </div>

      {/* A fixed width, so the buttons do not grow when an error appears below. */}
      <div className="flex min-h-[44px] w-full max-w-phi-12 flex-col items-center gap-phi-n1">
        <SignInButtons google={google} apple={apple} />

        {status === "loading" ? <p className="text-phi-n1 text-tx-2">Loading sign-in…</p> : null}
        {/* `status` is owned by the Google exchange, which has already succeeded
            by the time an adoption failure lands — so it stays "signing-in"
            for ever. Saying both at once tells the user it is still happening
            and that it failed. */}
        {status === "signing-in" && !failure ? (
          <p className="text-phi-n1 text-tx-2">Signing you in…</p>
        ) : null}
        {(status === "error" && error) || failure ? (
          <div className="flex flex-col items-center gap-phi-n3">
            <p role="alert" data-feedback="error" className="max-w-phi-12 text-phi-n1 text-over">
              {failure ?? error}
            </p>
            <GhostButton
              onClick={() => {
                clearFailure();
                retry();
              }}
            >
              Try again
            </GhostButton>
          </div>
        ) : null}
      </div>

      <p className="max-w-phi-12 text-phi-n1 text-tx-2">
        {apple.available ? "Sign in with Google or Apple." : "Sign in with Google."} Your expenses
        live in your account and sync to every
        device you sign in on — nothing else to set up.
      </p>
      <LegalLinks />
      {sheet}
    </div>
  );
}

export function InstallGateView(): React.JSX.Element {
  const { platform, canPrompt, prompt } = useInstallState();
  const leave = useSignOutFlow();
  const [busy, setBusy] = useState(false);
  const instructions = installInstructions(platform);

  const finishOnDesktop = (): void => {
    const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    void updateProfile({
      onboardedAt: new Date().toISOString(),
      ...(deviceZone ? { timezone: deviceZone } : {}),
    });
  };

  return (
    <div className="flex min-h-dvh flex-col justify-center gap-phi-3 bg-bg px-phi-3 py-phi-5">
      <Trident />

      <div className="flex flex-col gap-phi-n1">
        <Label>One last thing</Label>
        <h1 className="text-phi-2 font-bold tracking-tight">{instructions.title}</h1>
        <p className="text-phi-n1 text-tx-2">
          Tide Tally only keeps you signed in, and only keeps your expenses
          safe offline, once it lives on your Home Screen. Browsers clear a
          website&apos;s storage after about a week of not visiting — apps are
          exempt.
        </p>
      </div>

      <ol className="flex flex-col gap-phi-n1">
        {instructions.steps.map((step, index) => (
          <li key={step} className="flex gap-phi-n1 border-t border-line pt-phi-n1 text-phi-n1">
            <span className="tnum shrink-0 font-mono text-phi-n1 text-acc">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <div className="flex flex-col gap-phi-n1">
        {canPrompt ? (
          <PrimaryButton
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void prompt().finally(() => setBusy(false));
            }}
          >
            Install Tide Tally
          </PrimaryButton>
        ) : null}

        {platform === "desktop" ? (
          // Desktop browsers vary wildly in install support, and the 7-day
          // eviction is a Safari-on-iOS behaviour. Blocking here would strand
          // people for no safety gain.
          <GhostButton onClick={finishOnDesktop}>Continue in this browser</GhostButton>
        ) : (
          <p className="text-center text-phi-n1 text-tx-2">
            Open Tide Tally from your Home Screen to continue.
          </p>
        )}

        <GhostButton onClick={leave.begin}>
          {leave.busy ? "Signing out…" : "Sign out"}
        </GhostButton>
        {leave.error ? (
          <p role="alert" data-feedback="error" className="text-center text-phi-n1 text-over">
            {leave.error}
          </p>
        ) : null}
      </div>
      {leave.sheet}
    </div>
  );
}

/**
 * The third step — the monthly salary (SPEC.md §9.5).
 *
 * It sits **after** the install gate deliberately: on iOS the pre-install tab is
 * in storage Safari evicts after seven idle days, so a figure entered there
 * could be gone before it was ever pushed.
 *
 * It is not gated on `onboardedAt`. That flag is written automatically the
 * instant an installed shell loads (`App.tsx`), so it means "install
 * acknowledged" rather than "setup finished" — a user would be marked onboarded
 * before this screen could render, and skip straight past it. The gate is the
 * absence of a salary row, which is the thing itself rather than a marker for it.
 */
export function SalaryStepView(): React.JSX.Element {
  const { period } = useCurrentPeriod();
  const profile = useProfile();
  const leave = useSignOutFlow();
  const [raw, setRaw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    const amount = tryParseAmount(raw);
    if (amount === null) {
      setError(raw.trim() === "" ? "Enter your monthly salary." : `“${raw}” is not a valid amount.`);
      return;
    }
    setError(null);
    setBusy(true);
    // No `await` on the network — this is a Dexie write like any other, so it
    // completes in airplane mode and syncs later (INV-4).
    void setSalary(amount, periodStartDate(period, profile.timezone))
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error
            ? `Couldn't save that: ${cause.message}`
            : "Couldn't save that. Please try again.",
        );
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex min-h-dvh flex-col justify-center gap-phi-3 bg-bg px-phi-3 py-phi-5">
      <Trident />

      <div className="flex flex-col gap-phi-n1">
        <Label>Last step</Label>
        <h1 className="text-phi-2 font-bold tracking-tight">What do you take home each month?</h1>
        <p className="text-phi-n1 text-tx-2">
          The pay that lands in your account, after tax and CPF. Your budget is this amount, minus whatever you save first.
        </p>
      </div>

      <form
        className="flex flex-col gap-phi-n1"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label htmlFor="onboarding-salary" className="text-phi-n1 font-semibold">
          Monthly take-home pay
        </label>
        <div className="flex items-center rounded-phi-n2 border border-line bg-bg-2 px-phi-0">
        <span aria-hidden="true" className="fig text-phi-2">$</span>
        <input
          id="onboarding-salary"
          type="text"
          inputMode="decimal"
          autoFocus
          value={raw}
          onChange={(event) => setRaw(event.target.value)}
          placeholder="0.00"
          aria-describedby={error ? "onboarding-salary-error" : undefined}
          className="fig min-h-[44px] min-w-0 w-full bg-transparent text-right text-phi-0"
        />
        </div>
        <p className="text-phi-n1 text-tx-2">Not sure? Use your last payslip. You can change it any time in Settings › Budget.</p>
        {error ? (
          <p id="onboarding-salary-error" role="alert" data-feedback="error" className="text-phi-n1 text-over">
            {error}
          </p>
        ) : null}
        {/* `type="submit"` is not optional here: `PrimaryButton` defaults to
            "button", so without it the form's `onSubmit` never fires and this
            mandatory gate has no way past it. */}
        <PrimaryButton type="submit" disabled={busy}>
          Start logging
        </PrimaryButton>
      </form>

      <div className="flex flex-col gap-phi-n1">
        <p className="text-center text-phi-n1 text-tx-2">
          Past months keep the figure they were measured against.
        </p>
        <GhostButton onClick={leave.begin}>{leave.busy ? "Signing out…" : "Sign out"}</GhostButton>
        {leave.error ? <p role="alert" data-feedback="error" className="text-center text-phi-n1 text-over">{leave.error}</p> : null}
      </div>
      {leave.sheet}
    </div>
  );
}

function Trident(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 26 52"
      aria-hidden="true"
      className="mx-auto h-12 w-6 stroke-acc"
      fill="none"
      strokeWidth="1.4"
    >
      <path d="M13 52V14" />
      <path d="M3 4v10a10 10 0 0 0 20 0V4" />
      <path d="M13 14V2" />
      <path d="M3 4 1 8M23 4l2 4" />
      <path d="M7 44h12" />
    </svg>
  );
}
